// Org role constants — single source of truth for organisation-level role strings.
// These are the g2-grouping (domain=org_id) roles in the Casbin dual-domain model.
// Agent roles (g3-grouping) are defined per-agent in registry.ts.
//
// Ptype renamed g->g2 + role strings normalized to snake_case (ASC-5834):
// org:admin->org_admin, org:data_admin->data_admin, org:member->org_normal.
//
// Python mirror: shared/accesszen/accesszen/core/roles.py — keep in sync.

export const ORG_ADMIN = 'org_admin' as const
// Data-lens-scoped peer of ORG_ADMIN (RBAC v2 T3 / ASC-5644).
export const ORG_DATA_ADMIN = 'data_admin' as const
export const ORG_MEMBER = 'org_normal' as const

// Backwards-compatibility aliases (RBAC v2 removed these roles) — mirrors the
// Python side's ORG_OWNER/ORG_DEVELOPER/ORG_VIEWER aliases exactly.
export const ORG_OWNER = ORG_ADMIN
export const ORG_DEVELOPER = ORG_ADMIN
export const ORG_VIEWER = ORG_MEMBER

// The org-ASSIGNABLE roles, ordered highest → lowest privilege. Deliberately does
// NOT include PLATFORM_DEVELOPER — see below.
export const ORG_ROLES = [ORG_ADMIN, ORG_DATA_ADMIN, ORG_MEMBER] as const

export type OrgRole = (typeof ORG_ROLES)[number]

// ── platform_developer — a g2 role, but NOT an org-assignable one (ASC-9224) ──
//
// Sits ABOVE ORG_ADMIN in the Casbin g2 hierarchy (`g2, platform_developer,
// org_admin, *`), so it resolves everything org_admin does plus the two
// platform:developer p-rows. Until ASC-9224 it was a ptype-`g` flag orthogonal to
// org standing and inherited nothing, because cross-ptype inheritance does not
// chain.
//
// Kept out of ORG_ROLES on purpose: that array drives the role pickers, and
// ASC-9222 moved granting into the admin console so an org_admin cannot hand
// itself the developer tier. Adding it here would put it back in the dropdown.
export const PLATFORM_DEVELOPER_ROLE = 'platform_developer' as const

// Every role the g2 hierarchy resolves, assignable or not — mirrors
// accesszen.core.roles.ALL_G2_ROLES.
export const G2_ROLES = [PLATFORM_DEVELOPER_ROLE, ...ORG_ROLES] as const

export type G2Role = (typeof G2_ROLES)[number]
